// =====================================================
// App router — Phase-14 R3 BrowserRouter migration.
//
// History
// -------
// Until Phase 14, this repo used a hand-rolled hash router
// (`window.location.hash` + a switch statement in App.tsx). Hash
// routing hides every internal path from Google + breaks deep-link
// sharing for prerenderable pages. R3 switches to react-router-dom v6
// BrowserRouter without touching the 49 page components — they still
// accept the existing `go(id, param)` callback prop.
//
// The trick: `useGo()` returns a function with the exact old
// signature; internally it calls `useNavigate()`. Pages call
// `go("catalog")` exactly like before; the function now does
// `navigate("/catalog")` instead of `window.location.hash = "#catalog"`.
//
// Each route renders through `<RouteShell>`, which calls `useGo()`
// once per render and forwards `go` + the route's path param (if any)
// to the page. Param naming preserves the old prop names
// (`courseId`, `assessmentId`, `labId`) so pages don't change.
//
// Phase-14.5 C3: dropped @ts-nocheck. useGo's signature is now the
// authoritative typed contract for the 140 call sites; Phase 15
// per-page TS cleanup will extend the page-component types to consume
// it via `import type { Go } from "../router"`.
// =====================================================

import React from "react";
import { useTranslation } from "react-i18next";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { UIRoot } from "./ui";
import { AppShell } from "./layouts/AppShell";
// S-21 — RoleHomeRedirect resolves the active role's real home.
import { ROLES, useRole } from "./role";
// S-LAND-POLISH م۴ — گاردِ مسیرهای auth برای کاربرِ لاگین‌کرده.
import { useAuth } from "./auth/AuthContext";

// =====================================================
// Phase-A R7.1.b — route-level code splitting
// =====================================================
//
// Eagerly-imported files (stay in the main bundle):
//   - Home.tsx, Programs.tsx, Admissions.tsx  (PUBLIC entry points
//     that we need to paint fast on cold loads)
//   - auth/LoginPage.tsx                        (AUTH_FLOW entry point;
//     login is a frequent cold-load destination — keeping LoginPage
//     in the main chunk avoids a Suspense flicker on the most common
//     authed-user entry sequence)
//   - Roles.tsx, Academic.tsx                  (MIXED files containing
//     both PUBLIC + WORKSPACE exports. Kept eager for now; see the
//     S-CARD W0-cfg-b note below for why More.tsx is no longer among
//     them and why these two still are.)
//
// S-CARD W0-cfg-b (docs/review/S_CARD_W0CFG_PLAN.md §13) — More.tsx is now
// LAZY. The old note here claimed splitting the MIXED files "would require a
// file-level refactor". That premise was wrong: several React.lazy wrappers may
// point at the SAME module specifier, so a mixed file can be deferred whole
// without being split at all — Rollup still emits exactly one chunk for it.
// Deferring More.tsx reclaimed ~64 KiB from the eager entry chunk (it was the
// only eager importer of JalaliCalendar / JalaliDatePicker / i18n/jalali /
// ModalShell / PersonLink / BookmarkButton -> bookmark-state, so the whole
// subtree left with it), which is what bought the headroom for the
// /admin/membership-config page under the docs/BUNDLE_BASELINE.json quota.
// Accepted trade-off, owner-approved: a direct anonymous deep-link to the two
// PUBLIC routes in that file (/pricing, /help) now costs one chunk round-trip
// behind RouteShell's existing Persian Suspense fallback. /library, /faculty
// and /calendar/events are WORKSPACE, so they were never on a cold-paint path.
// Home.tsx / Programs.tsx / auth/LoginPage.tsx stay eager on purpose — they are the
// actual cold-load funnel. Roles.tsx + Academic.tsx were called out here as
// "the same free win (~20 KiB) if a later wave needs more headroom"; that wave
// arrived — S-FIX-FINANCE-2 بندِ ۰ took it (see the import block below). The
// eager cold-load funnel is now exactly Home / Programs / LoginPage.
//
// Lazy-imported files (each becomes its own chunk, downloaded on
// first navigation to a route inside the file):
//   - All purely-workspace single-page files (Dashboard, Classroom,
//     etc.) and the dedicated role-dashboards (SuperAdminDashboard,
//     ContentManagerDashboard, etc.).
//   - University.tsx + Productivity.tsx (no PUBLIC exports inside).
//   - Settings.tsx, Audit.tsx, etc.
//
// Each React.lazy(() => import(...)) creates a Rollup chunk boundary.
// The chunk hash is content-derived, so successive deploys that don't
// change a workspace page leave its chunk cached client-side.

import HomePage from "./pages/Home";
import ProgramsPage from "./pages/Programs";
// R-NAV-3 (spec §4.5) — the mock /admissions wizard (pages/Admissions) is
// retired; /admissions now redirects to the real /apply form, so the page is
// no longer imported here.
// R-NAV-2 — single shared "coming soon" page for every not-yet-built sidebar
// destination. Eager (tiny, no data) so the ~50 placeholder routes don't each
// spin up a Suspense boundary.
import ComingSoonPage from "./pages/ComingSoon";
import { PLACEHOLDER_ROUTES } from "./router/placeholder-routes";
// R-NAV-6 — real 404. Eager + mounted OUTSIDE the AppShell layout (see below)
// so unknown URLs show a 404 instead of the landing page, and anon visitors
// aren't bounced to /login by the workspace auth gate.
import NotFoundPage from "./pages/NotFound";
import LoginPage from "./pages/auth/LoginPage";
// Global i18n release: keep the common cold login eager, but load the other
// four auth screens only when visited. Named wrappers share one Auth chunk;
// existing RouteShell/Suspense, namespaces, guards and API behavior stay intact.
const ForgotPage = React.lazy(() => import("./pages/Auth").then((m) => ({ default: m.ForgotPage })));
const VerifyEmailPage = React.lazy(() => import("./pages/Auth").then((m) => ({ default: m.VerifyEmailPage })));
const TwoFactorPage = React.lazy(() => import("./pages/Auth").then((m) => ({ default: m.TwoFactorPage })));
const OnboardingPage = React.lazy(() => import("./pages/Auth").then((m) => ({ default: m.OnboardingPage })));
// S-FIX-FINANCE-2 بندِ ۰ — دو فایلِ MIXEDِ باقی‌مانده هم LAZY شدند؛ همان
// «بردِ رایگانِ ~۲۰ KiB» که یادداشتِ S-CARD W0-cfg-b بالا صریحاً برایِ موجِ
// بعدی کنار گذاشته بود. همان الگویِ More.tsx: چند wrapperِ React.lazy روی یک
// specifierِ واحد ⇒ Rollup برایِ هر فایل دقیقاً یک chunk می‌سازد، پس جابه‌جایی
// بینِ این پنج مسیر حداکثر یک دانلود دارد. هیچ Suspenseِ تازه‌ای اضافه نشد —
// هر پنج مسیر از قبل از `<RouteShell>` می‌گذرند که خودش `React.Suspense` با
// همان RouteLoadFallbackِ فارسی دارد.
// معاملهٔ پذیرفته‌شده (همان معاملهٔ More.tsx): دیپ‌لینکِ مستقیمِ ناشناس به این
// پنج مسیرِ PUBLIC یک رفت‌وبرگشتِ chunk هزینه می‌دهد. مسیرِ سردِ اصلی — `/`,
// `/programs`, `/login` — دست‌نخورده مانده. پیش‌بارگذاریِ namespaceِ i18n هم
// تحتِ تأثیر نیست: `firstPaintNamespaces` روی `pathname` کلید می‌خورد، نه روی
// chunk، پس `about` و `honor-code` همچنان در بوت namespaceشان را می‌گیرند.
const EventsPage = React.lazy(() =>
  import("./pages/Roles").then((m) => ({ default: m.EventsPage })),
);
const AboutPage = React.lazy(() =>
  import("./pages/Roles").then((m) => ({ default: m.AboutPage })),
);
const AlumniPage = React.lazy(() =>
  import("./pages/Academic").then((m) => ({ default: m.AlumniPage })),
);
const HackathonsPage = React.lazy(() =>
  import("./pages/Academic").then((m) => ({ default: m.HackathonsPage })),
);
const HonorCodePage = React.lazy(() =>
  import("./pages/Academic").then((m) => ({ default: m.HonorCodePage })),
);

// Lazy — deferred route files. Each `() => import(...)` becomes its
// own Rollup chunk. The named-export wrappers (`.then(m => ({ default: ... }))`)
// are needed for multi-export files; single-default-export files can
// use the shorter form.

// S-CARD W0-cfg-b — pages/More.tsx, deferred whole (see the header note).
// All five wrappers name the SAME specifier on purpose: Rollup emits ONE
// chunk for pages/More, not five, so navigating between any of these five
// routes costs at most a single download. No Suspense plumbing is added —
// every one of them is mounted through <RouteShell>, which already wraps its
// component in React.Suspense with the shared Persian RouteLoadFallback.
const CalendarPage = React.lazy(() =>
  import("./pages/More").then((m) => ({ default: m.CalendarPage })),
);
const LibraryPage = React.lazy(() =>
  import("./pages/More").then((m) => ({ default: m.LibraryPage })),
);
const HelpPage = React.lazy(() => import("./pages/More").then((m) => ({ default: m.HelpPage })));
const PricingPage = React.lazy(() =>
  import("./pages/More").then((m) => ({ default: m.PricingPage })),
);
const FacultyPage = React.lazy(() =>
  import("./pages/More").then((m) => ({ default: m.FacultyPage })),
);

const AssessmentLivePage = React.lazy(() => import("./pages/AssessmentLive"));
const ExamRoomPage = React.lazy(() => import("./pages/ExamRoom"));
const ProctorReviewPage = React.lazy(() => import("./pages/ProctorReview"));
const CatalogPage = React.lazy(() => import("./pages/Catalog"));
const CourseLivePage = React.lazy(() => import("./pages/CourseLive"));
const MyCoursesPage = React.lazy(() => import("./pages/MyCourses"));
const ProgressPage = React.lazy(() => import("./pages/Progress"));
// S-19 — /tutor now redirects into /agents (single assistant origin); the Tutor
// page component is retained on disk but no longer routed directly.
const AgentsPage = React.lazy(() => import("./pages/Agents"));
const CheckoutPage = React.lazy(() => import("./pages/Checkout")); // S9
const ClassroomPage = React.lazy(() => import("./pages/Classroom"));
// §12 — self-hosted WebRTC live class (NEW routes; additive, leaves /classroom intact).
const LiveLobbyPage = React.lazy(() => import("./pages/LiveLobby"));
const LiveSessionPage = React.lazy(() => import("./pages/LiveSession"));
const LiveClassesPage = React.lazy(() => import("./pages/LiveClasses"));
const SessionReportPage = React.lazy(() => import("./pages/SessionReport"));
const PlaybackRoomPage = React.lazy(() => import("./pages/PlaybackRoom"));
const WalletPage = React.lazy(() => import("./pages/Wallet"));
// S-21 — pages/Dashboard.tsx (the legacy mock) is no longer routed; /dashboard
// renders RoleHomeRedirect instead (see the route entry).
// S-21 — the course HUB (tabs: overview/content/sessions/assignments/exams/
// grades/attendance/files/group/agent) now owns /course/:courseId; the old
// content player (pages/Course.tsx) stays alive as the hub's «محتوا» tab
// (embedded mode — imported statically by CourseHub, same chunk).
const CourseHubPage = React.lazy(() => import("./pages/CourseHub"));
const InstructorPage = React.lazy(() => import("./pages/Instructor"));
const CredentialPage = React.lazy(() => import("./pages/CredentialsRich"));
// S-LANDING-2 — کاتالوگِ امکانات + صفحاتِ حقوقی/تماس/سؤالات. هر دو فایل
// لِیزی‌اند تا کاتالوگِ ۴۸‌تایی و متن‌های حقوقی روی چانکِ ایگر ننشینند.
const FeaturesIndexPage = React.lazy(() =>
  import("./pages/Features").then((m) => ({ default: m.FeaturesIndexPage })),
);
const FeatureDetailPage = React.lazy(() =>
  import("./pages/Features").then((m) => ({ default: m.FeatureDetailPage })),
);
const ContactPage = React.lazy(() =>
  import("./pages/Legal").then((m) => ({ default: m.ContactPage })),
);
const TermsPage = React.lazy(() => import("./pages/Legal").then((m) => ({ default: m.TermsPage })));
const PrivacyPage = React.lazy(() =>
  import("./pages/Legal").then((m) => ({ default: m.PrivacyPage })),
);
const FaqPage = React.lazy(() => import("./pages/Legal").then((m) => ({ default: m.FaqPage })));
// S-CRED W2 — «چاپِ دانشنامه» (/credential/print/:id). چانکِ جدا چون بومِ سند
// qrcode.react را می‌آورد و آن هرگز نباید در چانکِ اصلی بنشیند. کلیدِ نقشی لازم
// ندارد (مسیرهای بی‌کلید برای هر کاربرِ احرازشده باز است) و API خودش داده را به
// مالکِ رکورد محدود می‌کند: `/credentials/me` فقط ردیف‌های خودِ فراخوان را می‌دهد.
const DiplomaPrintPage = React.lazy(() => import("./pages/DiplomaPrint"));
const SearchPage = React.lazy(() => import("./pages/Search"));
const AssessmentPage = React.lazy(() => import("./pages/Assessment"));
const CommunityPage = React.lazy(() => import("./pages/CommunityRich"));
const AnalyticsPage = React.lazy(() => import("./pages/Analytics"));
const AuthoringPage = React.lazy(() => import("./pages/Authoring"));
const RecordingsPage = React.lazy(() => import("./pages/Recordings"));
const AuditPage = React.lazy(() => import("./pages/Audit"));
const SettingsPage = React.lazy(() => import("./pages/Settings"));
const SuperAdminDashboard = React.lazy(() => import("./pages/dashboards/SuperAdminDashboard"));
const ContentManagerDashboard = React.lazy(() => import("./pages/dashboards/ContentManagerDashboard"));
const TADashboard = React.lazy(() => import("./pages/dashboards/TADashboard"));
const SupportDashboard = React.lazy(() => import("./pages/dashboards/SupportDashboard"));
const ModeratorDashboard = React.lazy(() => import("./pages/dashboards/ModeratorDashboard"));
const FacultyAdminDashboard = React.lazy(() => import("./pages/dashboards/FacultyAdminDashboard"));
const ParentDashboard = React.lazy(() => import("./pages/dashboards/ParentDashboard"));
// Student-1 — real degree audit (replaces the Academic.tsx mock at /degree-audit).
const DegreeAuditPage = React.lazy(() => import("./pages/DegreeAudit"));
// چارت درسی من — the student's own گرایش-filtered degree chart (passed/in-progress/
// remaining by term), from GET /v1/registration/my-chart. Own lazy chunk.
const MyChartPage = React.lazy(() => import("./pages/MyChart"));
// Student services — rich, interactive sample-data pages (+ «در دست پیاده‌سازی» badge).
const OfficeHoursPage = React.lazy(() => import("./pages/OfficeHoursRich"));
const CareerPage = React.lazy(() => import("./pages/CareerRich"));
const WellnessPage = React.lazy(() => import("./pages/WellnessRich"));
// WELL-1 — the test-taking experience (intro+consent → questions+autosave →
// route to the result). Own lazy chunk (D66 Path D); /wellness dashboard stays.
const WellnessTestPage = React.lazy(() => import("./pages/WellnessTest"));
// WELL-1 — wellness test catalogue (grouped by category) + result detail +
// self-only result history. Each its own lazy chunk; /wellness dashboard stays.
const WellnessTestsPage = React.lazy(() => import("./pages/WellnessTests"));
const WellnessResultPage = React.lazy(() => import("./pages/WellnessResult"));
const WellnessResultsPage = React.lazy(() => import("./pages/WellnessResults"));
// S-ASSESS-CENTER (W2) — competency assessment (سنجش شایستگی), a DISTINCT
// catalogue from wellness self-tests. Catalogue (mounted at both /assess and
// /assess/tools) + take/attempt + self-only result detail + result history.
// Each its own lazy chunk.
const AssessToolsPage = React.lazy(() => import("./pages/AssessTools"));
const AssessToolPage = React.lazy(() => import("./pages/AssessTool"));
const AssessResultPage = React.lazy(() => import("./pages/AssessResult"));
const AssessResultsPage = React.lazy(() => import("./pages/AssessResults"));
// WELL-4 — «مشاور دانشگاه» (counselor) workspace. The least-privilege wellness
// role's home: own appointments + the tenant's open crisis queue + a scoped
// student wellness summary. Own lazy chunk; created by the WELL-4 fe agent.
const CounselorInboxPage = React.lazy(() => import("./pages/CounselorInbox"));
// R-MON W9 — counselor caseload / monitoring (کاربرگ مراجعان). The least-privilege
// reporting layer: an aggregate roll-up of the counselor's OWN caseload + a roster
// with a per-student continuity timeline + schedule-follow-up. All scoped server-side.
const CounselorCaseloadPage = React.lazy(() => import("./pages/CounselorCaseload"));
const CognitiveProfilePage = React.lazy(() => import("./pages/CognitiveProfile"));
const AdaptivePathPage = React.lazy(() => import("./pages/AdaptivePath"));
const AssignmentsPage = React.lazy(() => import("./pages/AssignmentsRich"));
// Instructor pages — rich interactive sample-data teaching tools (+ badge).
const InstructorDashboardPage = React.lazy(() => import("./pages/InstructorDashboard"));
const QuestionBankPage = React.lazy(() => import("./pages/QuestionBank"));
const AssessmentBuilderPage = React.lazy(() => import("./pages/AssessmentBuilder"));
const RubricBuilderPage = React.lazy(() => import("./pages/RubricBuilder"));
const ClassRosterPage = React.lazy(() => import("./pages/ClassRoster"));
const AttendancePage = React.lazy(() => import("./pages/Attendance"));
const ClassAnalyticsPage = React.lazy(() => import("./pages/ClassAnalytics"));
const ItemAnalysisPage = React.lazy(() => import("./pages/ItemAnalysis"));
const InstructorCopilotPage = React.lazy(() => import("./pages/InstructorCopilot"));
// Faculty-admin pages — rich interactive sample-data tools (+ badge).
const CourseCatalogMasterPage = React.lazy(() => import("./pages/CourseCatalogMaster"));
const InstructorAssignmentPage = React.lazy(() => import("./pages/InstructorAssignment"));
const ProbationReviewPage = React.lazy(() => import("./pages/ProbationReview"));
const FacultyReportsPage = React.lazy(() => import("./pages/FacultyReports"));
// Admin + super_admin pages.
const AdminDashboardPage = React.lazy(() => import("./pages/AdminDashboard"));
const RolesPermissionsPage = React.lazy(() => import("./pages/RolesPermissions"));
const AdminCalendarPage = React.lazy(() => import("./pages/AdminCalendar"));
const IntegrationsPage = React.lazy(() => import("./pages/Integrations"));
const NotifTemplatesPage = React.lazy(() => import("./pages/NotifTemplates"));
const BroadcastPage = React.lazy(() => import("./pages/Broadcast"));
const AiGovernancePage = React.lazy(() => import("./pages/AiGovernance"));
const SystemMonitoringPage = React.lazy(() => import("./pages/SystemMonitoring"));
const CapabilitiesStatusPage = React.lazy(() => import("./pages/CapabilitiesStatus"));
const AdminAnalyticsPage = React.lazy(() => import("./pages/AdminAnalytics"));
const TenantsAdminPage = React.lazy(() => import("./pages/TenantsAdmin"));
const ImpersonatePage = React.lazy(() => import("./pages/Impersonate"));
const MultitenancyPage = React.lazy(() => import("./pages/Multitenancy"));
// Support / TA / moderator / content_manager / org / parent pages.
const TicketsQueuePage = React.lazy(() => import("./pages/TicketsQueue"));
const UserLookupPage = React.lazy(() => import("./pages/UserLookup"));
const GradingQueuePage = React.lazy(() => import("./pages/GradingQueue"));
const FlaggedReportsPage = React.lazy(() => import("./pages/moderation/ModerationQueuePage"));
const CommunityModerationPage = React.lazy(() => import("./pages/CommunityModeration"));
const AutoRulesPage = React.lazy(() => import("./pages/moderation/AutoRulesPage"));
const ModerationLogPage = React.lazy(() => import("./pages/ModerationLog"));
const ContentReviewPage = React.lazy(() => import("./pages/ContentReview"));
const ContentAnalyticsPage = React.lazy(() => import("./pages/ContentAnalytics"));
const OrgUsersPage = React.lazy(() => import("./pages/OrgUsers"));
const OrgBulkEnrollPage = React.lazy(() => import("./pages/OrgBulkEnroll"));
const OrgSeatsPage = React.lazy(() => import("./pages/OrgSeats"));
const OrgInvoicesPage = React.lazy(() => import("./pages/OrgInvoices"));
const OrgReportsPage = React.lazy(() => import("./pages/OrgReports"));
const ParentGradesPage = React.lazy(() => import("./pages/ParentGrades"));
const ParentAttendancePage = React.lazy(() => import("./pages/ParentAttendance"));
const ParentSchedulePage = React.lazy(() => import("./pages/ParentSchedule"));
const ParentTuitionPage = React.lazy(() => import("./pages/ParentTuition"));
// Student-1b — real weekly schedule (replaces the ComingSoon at /schedule).
const WeeklySchedulePage = React.lazy(() => import("./pages/WeeklySchedule"));
// Student-1c — real academic calendar (active term's process windows: انتخاب واحد /
// حذف‌واضافه / امتحانات). Reachable at /academic-calendar.
const AcademicCalendarPage = React.lazy(() => import("./pages/AcademicCalendar"));
// R-CAL2 — تقویم یکپارچه. The unified, role-aware "my calendar" (meetings + exams +
// assignments + term windows + org events) that is now the PRIMARY /calendar surface
// for student/instructor/parent. The older events+weekly-schedule CalendarPage (R-CAL,
// from More.tsx) stays reachable at /calendar/events for org-event authoring.
const UnifiedCalendarPage = React.lazy(() => import("./pages/UnifiedCalendar"));
// Phase B R1 Commit I (D62 + D61 Constraint #2) — 4 admin pages lazy-
// loaded so they land in a separate admin-academic chunk (configured
// via manualChunks in vite.config.js, also this commit). Main bundle
// delta target: < 50 KB.
const SchoolsAdminPage = React.lazy(() => import("./pages/admin/SchoolsPage"));
const FacultiesAdminPage = React.lazy(() => import("./pages/admin/FacultiesPage"));
const DepartmentsAdminPage = React.lazy(() => import("./pages/admin/DepartmentsPage"));
const ProgramsAdminPage = React.lazy(() => import("./pages/admin/ProgramsPage"));
const PlansAdminPage = React.lazy(() => import("./pages/admin/PlansPage"));
// Phase B R2 Commit I (D65) — Offerings + Cohorts admin lazy routes.
// Lazy-loading places both pages in admin-academic.{hash}.js per D61
// Constraint #2; main bundle delta target: 0 KB.
const OfferingsAdminPage = React.lazy(() => import("./pages/admin/OfferingsPage"));
const CohortsAdminPage = React.lazy(() => import("./pages/admin/CohortsPage"));
// Phase B R3.a Commit I (D68 + D69) — Identity track admin lazy routes.
// Per D66 Path D: NO admin manualChunks bucket; each page becomes its
// own ProfilesPage-<hash>.js / StudentsPage-<hash>.js / InstructorsPage-<hash>.js
// — none preloaded on anon routes.
const ProfilesAdminPage = React.lazy(() => import("./pages/admin/ProfilesPage"));
const StudentsAdminPage = React.lazy(() => import("./pages/admin/StudentsPage"));
const InstructorsAdminPage = React.lazy(() => import("./pages/admin/InstructorsPage"));
// Phase B R3.b Commit G (D71) — Applications inbox (unified, Q9.a).
// Same per-route lazy pattern; ships as ApplicationsPage-<hash>.js.
const ApplicationsAdminPage = React.lazy(() => import("./pages/admin/ApplicationsPage"));
// S-18 — HR / hiring (استخدام) console; own per-route chunk.
const HrAdminPage = React.lazy(() => import("./pages/admin/HrPage"));
// S-4/5 موج ۲ — regulation-policy (آیین‌نامهٔ آموزشی) admin console; own per-route chunk.
const RegulationAdminPage = React.lazy(() => import("./pages/admin/RegulationPage"));
// Phase B R4 Commit E (D73) — Enrollments admin (closes D72 spine gap).
// Own per-route lazy chunk (D66 Path D).
const EnrollmentsAdminPage = React.lazy(() => import("./pages/admin/EnrollmentsPage"));
// Phase C A.1 (D91) — course content authoring (modules + lessons). Own
// per-route lazy chunk (D66 Path D), not preloaded on anon routes.
const CourseContentAdminPage = React.lazy(() => import("./pages/admin/CourseContentPage"));
// R-SIS-1 — academic terms (نیم‌سال) admin. Own per-route lazy chunk.
const TermsAdminPage = React.lazy(() => import("./pages/admin/TermsPage"));
// R-SIS-2 — curriculum builder (چارت درسی). Own per-route lazy chunk.
const CurriculumBuilderPage = React.lazy(() => import("./pages/CurriculumBuilder"));
// R-SIS-3d — انتخاب واحد + حذف و اضافه (one page, mode from the route).
const RegistrationPage = React.lazy(() => import("./pages/Registration"));
// R-SIS-5b — service desk (inbox/new/detail in one page) + workflow builder.
const ServiceDeskPage = React.lazy(() => import("./pages/ServiceDesk"));
const WorkflowsAdminPage = React.lazy(() => import("./pages/admin/WorkflowsPage"));
// R-SIS — degree policies (caps + probation thresholds) admin.
const DegreePoliciesAdminPage = React.lazy(() => import("./pages/admin/DegreePoliciesPage"));
// Phase C A.4 (D93) — role-aware gradebook. Own lazy chunk (D66 Path D).
const GradebookPage = React.lazy(() => import("./pages/Gradebook"));
// Phase C A.5 (D98) — student transcript. Own lazy chunk (D66 Path D).
const TranscriptPage = React.lazy(() => import("./pages/Transcript"));
// R-PAY-1d — student tuition + admin tariff config.
const TuitionPage = React.lazy(() => import("./pages/Tuition"));
const TuitionConfigAdminPage = React.lazy(() => import("./pages/admin/TuitionConfigPage"));
// S-CARD W0-cfg-b گامِ ۲ — تنظیماتِ عضویت (پول/برنامه/طرح‌ها/تاریخچه).
const MembershipConfigPage = React.lazy(() => import("./pages/admin/MembershipConfigPage"));
// S-AI-PROVIDER-1 — میزِ مدیریتِ مصرف/هزینهٔ AI (فقط‌خواندنی).
const AiUsagePage = React.lazy(() => import("./pages/admin/AiUsagePage"));
// S-CARD فاز A W5 — «عضویتِ من» (self-service) + «صفِ عضویت» (کنسولِ اپراتور).
const MembershipPage = React.lazy(() => import("./pages/Membership"));
// S-CARD W4 — دایرکتوریِ اعضا (WORKSPACE، هر کاربرِ احرازشده، گیتِ واقعی سمتِ سرور).
const MembershipDirectoryPage = React.lazy(() => import("./pages/MembershipDirectory"));
const MembershipQueuePage = React.lazy(() => import("./pages/admin/MembershipQueuePage"));
// S-ASSESS-W3b — کنسولِ ارزیاب (تخصیص‌های امروز) + جزئیاتِ تمرین/فرمِ نمره‌دهی
// + سفرِ خودِ داوطلب (self-service) + کنسولِ مدیرِ کانون.
const AssessorConsolePage = React.lazy(() => import("./pages/AssessorConsole"));
const AssessExerciseRosterPage = React.lazy(() => import("./pages/AssessExerciseRoster"));
const AssessRatingPage = React.lazy(() => import("./pages/AssessRating"));
const AssessCandidateJourneyPage = React.lazy(() => import("./pages/AssessCandidateJourney"));
const AssessCenterAdminPage = React.lazy(() => import("./pages/admin/AssessCenterAdminPage"));
// S-ASSESS-W4b — اتاقِ جلسهٔ واش‌آپ (مسیرِ جداگانه، سبدِ خودش).
const AssessWashupRoomPage = React.lazy(() => import("./pages/AssessWashupRoom"));
// S-ASSESS-W5b — گزارشِ منتشرشدهٔ خودِ داوطلب (ر۱) + گزارشِ داخلیِ کاملِ مدیر (ر۲).
const AssessMyReportPage = React.lazy(() => import("./pages/AssessMyReport"));
const AssessManagerReportPage = React.lazy(() => import("./pages/AssessManagerReport"));
// R-PAY-2c — student loans + welfare review (role-switched in the page).
const LoansPage = React.lazy(() => import("./pages/Loans"));
// R-PAY-2d — financial clearance (graduation prerequisite).
const ClearancePage = React.lazy(() => import("./pages/Clearance"));
// R-PAY-2e — scholarships / financial aid (role-switched in the page).
const FinancialAidPage = React.lazy(() => import("./pages/FinancialAid"));
// R-PAY-2f — tuition refunds (role-switched in the page).
const RefundPage = React.lazy(() => import("./pages/Refund"));
// R-PAYROLL-1c — payslips (personnel) + decrees admin.
const PayslipsPage = React.lazy(() => import("./pages/Payslips"));
const PayrollDecreesPage = React.lazy(() => import("./pages/admin/PayrollDecrees"));
const PayrollConfigPage = React.lazy(() => import("./pages/admin/PayrollConfig"));
// R-PAYROLL-2a — teaching/supervision claims (instructor + manager queue).
const ClaimsPage = React.lazy(() => import("./pages/Claims"));
const TeachingClaimsPage = React.lazy(() => import("./pages/admin/TeachingClaims"));
// R-PAYROLL-2b — monthly payroll run (manager).
const PayrollRunPage = React.lazy(() => import("./pages/admin/PayrollRun"));
// R-PAYROLL-2c — treasury + reports (manager).
const TreasuryPage = React.lazy(() => import("./pages/admin/Treasury"));
// S-W5/S-W6 — bank withdrawal approval queue (finance_manager / super_admin).
const WithdrawalQueuePage = React.lazy(() => import("./pages/admin/WithdrawalQueue"));
// R-SIS-6 — کمیسیون موارد خاص.
const CommissionPage = React.lazy(() => import("./pages/admin/Commission"));
const MyPathPage = React.lazy(() => import("./pages/MyPath"));
const GradeSubmissionPage = React.lazy(() => import("./pages/GradeSubmission"));
const MyGradesPage = React.lazy(() => import("./pages/MyGrades"));
// R-COMPLY W3 — student's own attendance + the آیین‌نامه ۳/۱۶ over-threshold
// WARNING (flag only; حذف درس stays a human/commission decision). Own lazy chunk.
const MyAttendancePage = React.lazy(() => import("./pages/MyAttendance"));
const StageProgressAdminPage = React.lazy(() => import("./pages/admin/StageProgress"));
// R-SIS-7 — People directory.
const PeoplePage = React.lazy(() => import("./pages/admin/People"));
// R-MON W6 — «پروندهٔ افراد»: the role-aware person-360 by userId (student =
// transcript + audit + exams/assignments/attendance + activity; instructor =
// sections + load + grading + activity). admin/super_admin/faculty_admin
// (faculty-scoped server-side). The default export is PersonOverviewPage.
const StudentOverviewPage = React.lazy(() => import("./pages/admin/StudentOverview"));
// R-ACAD — گرایش (specialization) per-program management. Own lazy chunk.
const SpecializationsPage = React.lazy(() => import("./pages/admin/Specializations"));
// R-ACAD — داده‌های مرجع (FieldGroup + DegreeLevel CRUD). Own lazy chunk.
const ReferenceDataPage = React.lazy(() => import("./pages/admin/ReferenceData"));
// chart-manual.md — کتابخانه وزارت علوم (3268 PDF ministry curricula browser + instantiate).
const MinistryLibraryPage = React.lazy(() => import("./pages/admin/MinistryLibrary"));
const MinistryProgramDetailPage = React.lazy(() => import("./pages/admin/MinistryProgramDetail"));
// §9.3 fast-follow — admin user management (CRUD + role assignment).
const UsersPage = React.lazy(() => import("./pages/admin/UsersPage"));
// R-MON W4 — admin parent↔student link onboarding (the runtime way to connect a
// real والد to a دانشجو; previously seed-only). Own per-route lazy chunk.
const ParentLinksPage = React.lazy(() => import("./pages/admin/ParentLinks"));
const OrgDashboard = React.lazy(() => import("./pages/dashboards/OrgDashboard"));
// University.tsx + Productivity.tsx are multi-export, all workspace:
const SchoolsPage = React.lazy(() => import("./pages/University").then((m) => ({ default: m.SchoolsPage })));
const LabsPage = React.lazy(() => import("./pages/LabsRich"));
const ResearchPage = React.lazy(() => import("./pages/University").then((m) => ({ default: m.ResearchPage })));
const NotificationsPage = React.lazy(() => import("./pages/Productivity").then((m) => ({ default: m.NotificationsPage })));
// R-CHAT-1b — real messenger (replaces the Productivity mock).
const MessagesPage = React.lazy(() => import("./pages/Messages"));
const BookmarksPage = React.lazy(() => import("./pages/Productivity").then((m) => ({ default: m.BookmarksPage })));
// Student-2 — real achievements (academic milestones) + portfolio (completed
// coursework), replacing the Productivity mock + the ComingSoon placeholder.
const AchievementsPage = React.lazy(() => import("./pages/Achievements"));
const PortfolioPage = React.lazy(() => import("./pages/Portfolio"));
const SubmissionPage = React.lazy(() => import("./pages/Productivity").then((m) => ({ default: m.SubmissionPage })));
// Phase B R3.a Commit H (D68 + D69) — /profile rerouted to the new
// self-service editor at pages/ProfilePage.tsx. The legacy mockup at
// pages/Productivity.tsx ProfilePage export stays in place (dead-code
// once unreferenced; left there to keep Productivity.tsx untouched in
// this commit). Per D66 Path D: own lazy chunk, NO admin bucket.
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
// R-SETTINGS — PUBLIC privacy-gated profile card at /u/:userId. Own lazy chunk;
// route-classification marks the "u" segment PUBLIC so a guest reaches it without
// being bounced to /login (the page + API enforce the owner's privacy settings).
const PublicProfilePage = React.lazy(() => import("./pages/PublicProfile"));
// R-CONNECT — «شبکهٔ من»: the connect hub (/network, authed). Own lazy chunk;
// route-classification leaves it WORKSPACE (auth-gated) by elimination.
const NetworkPage = React.lazy(() => import("./pages/Network"));
// Phase B R6 (D80) — public anon /apply form. Lazy per D66 Path D (not
// cold-paint-critical; a brief Suspense fallback is acceptable). Own
// content-hashed chunk, NOT eager → main-bundle Δ ≈ 0.
const ApplyPage = React.lazy(() => import("./pages/Apply"));
// Phase B R6 (D80) — public anon /track status page (token-based read +
// withdraw). Lazy per D66 Path D; own content-hashed chunk.
const TrackPage = React.lazy(() => import("./pages/Track"));
// R-Notif (D100) — public password-claim landing (/claim?token=). Lazy per
// D66 Path D; own content-hashed chunk, not preloaded on anon routes.
const ClaimPage = React.lazy(() => import("./pages/Claim"));
// R-AUTH-3 — public password-reset landing (/reset?token=). Lazy; own chunk.
const ResetPage = React.lazy(() => import("./pages/Reset"));
// S-SMS-OTP — phone-verification onboarding page. WORKSPACE route (not in
// PUBLIC_ROUTE_IDS/AUTH_FLOW_ROUTE_IDS) so AppShell's auth gate applies as-is.
const VerifyPhonePage = React.lazy(() => import("./pages/VerifyPhone"));
// R-NAV-4 (spec §3.5) — the mobile bottom-nav "more" hub. Lazy; its own chunk.
const MoreHubPage = React.lazy(() => import("./pages/MoreHub"));
// «دربارهٔ ویستا» (/vista) — صفحهٔ عمومیِ مجریِ فنی. Lazy و چانکِ مستقل: یک
// صفحهٔ معرفیِ کم‌تردد نباید در باندلِ اولِ لندینگ بنشیند.
const VistaPage = React.lazy(() => import("./pages/Vista"));
// R-MARKET P1 — «بازار دانش» authoring studio (course structure only). Two own
// lazy chunks; author-role gated via route-roles.ts ("market" key). The public
// marketplace browse tab is P4 (not added here).
const MarketStudioPage = React.lazy(() => import("./pages/market/Studio"));
// S-MKT-PRO-W1 — the routed studio SHELL (7 tabs) replacing the CourseEditor
// monolith at /market/studio/:id (+ /:tab), and the گام‌به‌گام creation wizard
// at /market/studio/new. Both own lazy chunks; the author "market" subtree role
// key gates them exactly like the old editor.
const MarketStudioShellPage = React.lazy(() => import("./pages/market/studio/StudioLayout"));
const MarketCreateWizardPage = React.lazy(() => import("./pages/market/studio/CreateWizard"));
// R-MARKET P2 — author "preview as a student" page. Own lazy chunk; same
// "market" route-roles key gates it (the whole /market/* subtree).
const MarketCoursePreviewPage = React.lazy(() => import("./pages/market/CoursePreview"));
// R-MARKET P3 — reviewer (content_manager) queue + per-course review page. Own
// lazy chunks; gated to content_manager/admin/super_admin by the "market/review"
// 2-segment key in route-roles.ts (it overrides the author "market" subtree key).
const MarketReviewQueuePage = React.lazy(() => import("./pages/market/ReviewQueue"));
const MarketReviewCoursePage = React.lazy(() => import("./pages/market/ReviewCourse"));
// R-MARKET P4 — the PUBLIC marketplace: catalog (/market) + a course's public
// detail (/market/course/:id). Reachable LOGGED-OUT (route-classification.ts marks
// the bare /market + /market/course/* PUBLIC; the studio/review subtrees stay
// WORKSPACE). Own lazy chunks; no role key (anyone, incl. a guest, may browse).
const MarketplacePage = React.lazy(() => import("./pages/market/Marketplace"));
const MarketCourseDetailPage = React.lazy(() => import("./pages/market/CourseDetail"));
// S-MKT-PRO-W2 — PUBLIC instructor showcase («ویترین مدرس»).
const MarketInstructorProfilePage = React.lazy(() => import("./pages/market/InstructorProfile"));
// R-MARKET P5 — pricing/purchase/economics surfaces. «دوره‌های من» (buyer; any
// authed user), «درآمد من» (author), «مدیریت بازار» (admin). Own lazy chunks; the
// "market/my-courses" + "market/earnings" + "market/admin" route-roles keys gate
// them (they win over the public "market" subtree), and route-classification keeps
// them WORKSPACE (auth-gated) — they sit under the /market segment.
const MarketMyCoursesPage = React.lazy(() => import("./pages/market/MyCourses"));
// S-MKT-PRO-W4 — «خریدهای من»: purchase history + printable receipt (/market/purchases).
// Own lazy chunk; the "market/purchases" route-roles key opens it to ANY authed role and
// route-classification keeps it WORKSPACE (auth-gated). The API self-scopes every row.
const MarketPurchasesPage = React.lazy(() => import("./pages/market/Purchases"));
// S-MKT-PRO-W8 — «تراکنش‌های من»: the ONE server-merged money timeline over purchases,
// wallet, settlements, withdrawals and clawback deductions (/market/transactions). Own
// lazy chunk; the "market/transactions" route-roles key opens it to ANY authed role and
// route-classification keeps it WORKSPACE (auth-gated). The API self-scopes every row and
// returns a role-aware `sections` map, so the page never shows a tab the caller has no
// money in.
const MarketTransactionsPage = React.lazy(() => import("./pages/market/Transactions"));
const MarketEarningsPage = React.lazy(() => import("./pages/market/Earnings"));
const MarketAdminPage = React.lazy(() => import("./pages/market/MarketAdmin"));
// S-STUDIO-P1-W1 — ذخیره‌سازیِ استودیو (سوپرادمین/ادمین)، همان الگویِ MarketAdminPage.
const StudioStorageAdminPage = React.lazy(() => import("./pages/market/StudioStorageAdmin"));
// S-LIVE-P1-W1 — سقفِ ظرفیتِ کلاسِ زنده (سوپرادمین/ادمین)، همان الگویِ MarketAdminPage.
const LiveClassCapacityAdminPage = React.lazy(() => import("./pages/LiveClassCapacityAdmin"));
// R-MARKET EXT — «علاقه‌مندی‌های من» (the buyer's wishlist, /market/wishlist). Own lazy
// chunk; the "market/wishlist" route-roles key opens it to ANY authed role (every role
// may save a course), and route-classification keeps it WORKSPACE (auth-gated).
const MarketWishlistPage = React.lazy(() => import("./pages/market/Wishlist"));
// S-MKT-PRO-W9 — «سبد خرید» (the buyer's cart, /market/cart). Own lazy chunk; the
// "market/cart" route-roles key opens it to ANY authed role (every role may buy a course),
// and route-classification keeps it WORKSPACE (auth-gated).
const MarketCartPage = React.lazy(() => import("./pages/market/Cart"));
// R-MARKET P6 — the enrolled buyer's COURSE PLAYER (/market/learn/:id). Own lazy chunk;
// the "market/learn" route-roles key opens it to any authed role (the API enforces
// enrollment, 404 for non-buyers) and route-classification keeps it WORKSPACE (auth-gated).
const MarketCoursePlayerPage = React.lazy(() => import("./pages/market/CoursePlayer"));
// S-MKT-PRO-E2 — the graduate's PRINT-READY «مدرک من» (/market/certificate/:id). Its own lazy
// chunk on purpose: it pulls in qrcode.react, which must stay out of the main bundle. Same
// gating premise as the player — "market/certificate" role key (any authed role) + WORKSPACE
// classification; the API 404s anyone without an enrollment AND an issued certificate.
const MarketCertificatePage = React.lazy(() => import("./pages/market/CertificatePrint"));
// S-MKT-PRO-E4 — the buyer's «کارنامهٔ من» for one purchased course (/market/report-card/:id).
// Own lazy chunk; same gating premise as the player — "market/report-card" role key (any authed
// role) + WORKSPACE classification, and the API 404s anyone without an active enrollment.
const MarketReportCardPage = React.lazy(() => import("./pages/market/ReportCard"));
// R-MARKET P7 (FINAL) — the author's in-browser RECORDING STUDIO (/market/studio/:id/record).
// Own lazy chunk; the author "market" subtree role key gates it (more-specific :id/record route
// resolves ahead of /market/studio/:id). The API enforces course ownership (404 else).
const MarketRecordStudioPage = React.lazy(() => import("./pages/market/RecordStudio"));
// S-MKT-PRO-W3 — «پریمیر-لایت» فاز ۱: the non-destructive EDL timeline editor
// (/market/studio/:id/edit/:assetId). Own lazy chunk (canvas ruler + filmstrip decoding
// have no business in the main chunk); the author "market" subtree role key gates it, and
// the API 404s a foreign course/asset/project.
const MarketEditProjectPage = React.lazy(() => import("./pages/market/studio/edit/EditProjectPage"));
// R-MARKET BUNDLES — «بستهٔ دوره» course bundles. The AUTHOR studio list
// (/market/studio/bundles) + bundle builder (/market/studio/bundles/:id) sit under
// the author /market/studio/* subtree (the "market" role key gates them; the more-
// specific /bundles paths resolve ahead of /market/studio/:id). The PUBLIC catalog
// (/market/bundles) + bundle detail (/market/bundles/:id) are reachable LOGGED-OUT —
// "bundles" is NOT an AUTHED_MARKET_SUBPATH, so route-classification keeps them PUBLIC
// like the course catalog. The API @Roles-guards the author routes (404 cross-author).
// R-MARKET OPEN-RESPONSE (Gap 9) — the author's STAFF grading queue for one course
// (/market/grading-queue/:id). Own lazy chunk; gated to the author roles by the
// "market/grading-queue" key in route-roles.ts (the API @Roles-guards + 404s cross-author).
const MarketGradingQueuePage = React.lazy(() => import("./pages/market/GradingQueuePage"));
const MarketStudioBundlesPage = React.lazy(() => import("./pages/market/StudioBundles"));
const MarketBundleEditorPage = React.lazy(() => import("./pages/market/BundleEditor"));
const MarketBundlesPage = React.lazy(() => import("./pages/market/Bundles"));
const MarketBundleDetailPage = React.lazy(() => import("./pages/market/BundleDetail"));

// =====================================================
// useGo — back-compat shim
// =====================================================
//
// The old App.tsx exposed `go(id, param)` to every page; ~140 call
// sites use the signature. We preserve it exactly so no page changes
// during R3 — only App.tsx and the new router.tsx wire react-router.
//
// Future sprints can migrate call sites to useNavigate() directly,
// but that's per-page polish, not the critical path here.
//
// `param` is optional and accepts string | number for ergonomics
// (route ids like course/abc-123 come from string ids; tutorial IDs
// might be numeric in some pages). It's String()-coerced before use.

/**
 * Type of the navigation function passed as `go` prop throughout the
 * app. Pages import this as `import type { Go } from "../router"`.
 */
export type Go = (id: string, param?: string | number | null) => void;

export const useGo = (): Go => {
  const navigate = useNavigate();
  return React.useCallback<Go>(
    (id, param) => {
      const safe = String(id || "home").replace(/^[#/]+/, "");
      const path =
        "/" + safe + (param != null && param !== "" ? "/" + encodeURIComponent(String(param)) : "");
      navigate(path);
      // Mirror the old behaviour: scroll to top on every nav so pages
      // start fresh, never inheriting the previous scroll position.
      try {
        window.scrollTo({ top: 0, behavior: "instant" });
      } catch {
        window.scrollTo(0, 0);
      }
    },
    [navigate]
  );
};

// =====================================================
// useCurrentRoute — mimics the old { id, param } shape
// =====================================================
//
// A few components (Nav, the ErrorBoundary `key`) need the active
// route id + param to drive aria-current / focus-reset semantics.
// We compute it from useLocation() so it stays in sync with whatever
// the router thinks the current URL is.
export interface CurrentRoute {
  id: string;
  param: string | null;
}

export const useCurrentRoute = (): CurrentRoute => {
  const location = useLocation();
  const path = location.pathname.replace(/^\/+/, "");
  if (!path) return { id: "home", param: null };
  const [id, ...rest] = path.split("/");
  return {
    id: id || "home",
    param: rest.length ? decodeURIComponent(rest.join("/")) : null,
  };
};

// =====================================================
// RouteShell — injects `go` + path param into a page
// =====================================================
//
// Each route's element is `<RouteShell Component={SomePage} paramKey="courseId" />`.
// The shell calls hooks once, then renders the page with the same
// prop names the page already uses. paramKey is omitted for routes
// without a URL param.
//
// `Component` is typed as `React.ComponentType<any>` rather than a
// precise prop shape because pages haven't been individually typed
// yet (Phase 15+ retires more @ts-nocheck per file). When the page
// types land, swap to a generic-typed RouteShell<P>.
interface RouteShellProps {
  Component: React.ComponentType<any>;
  paramKey?: string;
}

// Phase-A R7.1.b — Suspense fallback for lazy-loaded route components.
// React.lazy components throw a promise on first render; the Suspense
// boundary catches it and renders the fallback until the chunk loads.
// Eagerly-imported components render synchronously and never trigger
// the Suspense — so the eager PUBLIC + AUTH_FLOW routes are unaffected.
//
// The fallback uses Tailwind utilities for centering + a subtle pulse
// animation. Keep it lightweight (no Radix, no third-party) so it
// itself ships in the main bundle and renders instantly. The styling
// is intentionally close to "blank workspace surface" so the user
// reads it as "loading" not as a content state.
const RouteLoadFallback: React.FC = () => {
  const { t } = useTranslation("common");
  const loadingLabel = t("state.loading");

  return (
    <div
      className="flex items-center justify-center min-h-[60vh] text-[color:var(--fg-mute)] animate-pulse"
      role="status"
      aria-live="polite"
      aria-label={loadingLabel}
    >
      <div className="text-sm">{loadingLabel}</div>
    </div>
  );
};

// S-21 — /dashboard (legacy mock) → the active role's REAL home. None of the
// 14 homeRoutes is "dashboard" (verified by the nav-coherence vitest), so this
// can't loop; an anonymous visitor falls to the auth gate via /login.
const RoleHomeRedirect: React.FC = () => {
  const go = useGo();
  const { activeRoleId } = useRole();
  React.useEffect(() => {
    go(activeRoleId ? (ROLES[activeRoleId]?.homeRoute ?? "progress") : "login");
  }, [go, activeRoleId]);
  return <RouteLoadFallback />;
};

// S-LAND-POLISH م۴ — نیمهٔ دومِ بازخوردِ مالک: «بعد از لاگین دیگر نباید
// بتواند وارد صفحهٔ لاگین شود». ریدایرکتِ landing→dashboard در AppShell
// برداشته شد؛ در عوض مسیرهای AUTH_FLOW برای کاربرِ احرازشده به خانهٔ
// نقشِ خودش می‌روند. همان قراردادِ ضدِ حلقهٔ RoleHomeRedirect: هیچ
// homeRoute برابرِ "login"/"register" نیست (tests/nav/nav-coherence).
const RedirectAuthedHome: React.FC = () => {
  const go = useGo();
  const { activeRoleId } = useRole();
  React.useEffect(() => {
    go(activeRoleId ? (ROLES[activeRoleId]?.homeRoute ?? "progress") : "progress");
  }, [go, activeRoleId]);
  return <RouteLoadFallback />;
};

/** مسیرِ auth را فقط به مهمان نشان می‌دهد؛ کاربرِ لاگین‌کرده به داشبورد می‌رود. */
/* export فقط برای تستِ م۴ (tests/landing/landing-auth-visibility.test.tsx) —
   منطقِ گارد در همین یک نقطه می‌ماند و تست نسخهٔ دومی از آن نمی‌سازد. */
export const GuestOnly: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const auth = useAuth();
  if (auth.isAuthenticated) return <RedirectAuthedHome />;
  return children;
};

const RouteShell = ({ Component, paramKey }: RouteShellProps): React.ReactElement => {
  const go = useGo();
  const params = useParams();
  const extra: Record<string, string | undefined> = paramKey
    ? { [paramKey]: params[paramKey] }
    : {};
  return (
    <React.Suspense fallback={<RouteLoadFallback />}>
      <Component go={go} {...extra} />
    </React.Suspense>
  );
};

// =====================================================
// Route classification + Layout
// =====================================================
//
// Phase-A R1.1: the inline Layout + route classification that lived
// here were extracted. Layout → apps/web/src/layouts/AppShell.tsx (3-mode
// shell with adaptive sidebar). Route classification →
// apps/web/src/router/route-classification.ts (getRouteKind +
// isPublicRoute / isAuthFlowRoute / isWorkspaceRoute helpers).
//
// We still wrap AppShell in UIRoot here so theme / toast / confirm /
// command-palette / AI FAB context is available everywhere. UIRoot
// previously lived inside Layout; pulling it up means AppShell's three
// modes can compose without owning the global context, and any future
// shell variant (e.g., a print-only shell) gets the same context for free.
const LayoutWithChrome: React.FC = () => {
  const go = useGo();
  const { isAuthenticated } = useAuth();
  const { activeRoleId } = useRole();
  return (
    <UIRoot onNavigate={go} authenticated={isAuthenticated} activeRoleId={activeRoleId}>
      <AppShell />
    </UIRoot>
  );
};

// =====================================================
// Route table
// =====================================================
//
// 1:1 with the old `switch (route)` in App.tsx. Three routes carry
// a URL param: `/course-live/:courseId`, `/assessment-live/:assessmentId`,
// `/course/:courseId`, `/virtuallab/:labId`. The catch-all `*` falls
// back to HomePage (matches the old default branch).
//
// Ordering matters for `path="*"` but not for the others (react-router
// resolves the most-specific match first).
// exported for tests/live/route-shell.guard.test.ts (walks the real table —
// no hand-maintained duplicate of the route list).
export const routes = [
  { path: "/", element: <RouteShell Component={HomePage} /> },
  { path: "/home", element: <RouteShell Component={HomePage} /> },
  { path: "/programs", element: <RouteShell Component={ProgramsPage} /> },
  { path: "/catalog", element: <RouteShell Component={CatalogPage} /> },
  { path: "/my-courses", element: <RouteShell Component={MyCoursesPage} /> },
  {
    path: "/course-live/:courseId",
    element: <RouteShell Component={CourseLivePage} paramKey="courseId" />,
  },
  {
    path: "/assessment-live/:assessmentId",
    element: (
      <RouteShell Component={AssessmentLivePage} paramKey="assessmentId" />
    ),
  },
  {
    // W6 — proctored exam room (timed + identity + anti-cheat).
    path: "/exam/:assessmentId",
    element: <RouteShell Component={ExamRoomPage} paramKey="assessmentId" />,
  },
  {
    // R-EXAM-3 (§13.6) — proctor review queue (human-in-the-loop) + item analysis.
    path: "/proctor-review/:assessmentId",
    element: <RouteShell Component={ProctorReviewPage} paramKey="assessmentId" />,
  },
  { path: "/progress", element: <RouteShell Component={ProgressPage} /> },
  // S-19 — the standalone AI tutor page was a near-duplicate of /agents (owner
  // complaint #6). The RAG tutor now lives INSIDE the hub as the «پرسش از جزوه‌ها»
  // kind; /tutor redirects there so old links + the ⌘K/breadcrumb registries stay
  // valid. The feature is preserved (not removed) — just single-origin now.
  { path: "/tutor", element: <Navigate to="/agents?kind=tutor" replace /> },
  { path: "/agents", element: <RouteShell Component={AgentsPage} /> },
  { path: "/classroom", element: <RouteShell Component={ClassroomPage} /> },
  // §12 — live class: sessions hub (list) → lobby (device test) → in-session room.
  { path: "/live-class", element: <RouteShell Component={LiveClassesPage} /> },
  // More-specific /lobby is listed before the bare :sessionId so it matches first.
  {
    path: "/live-class/:sessionId/lobby",
    element: <RouteShell Component={LiveLobbyPage} paramKey="sessionId" />,
  },
  // MS-17 — the post-session «گزارش کلاس» (staff/host only; the endpoint 404s
  // for anyone else). Listed before the bare :sessionId so /report matches first.
  {
    path: "/live-class/:sessionId/report",
    element: <RouteShell Component={SessionReportPage} paramKey="sessionId" />,
  },
  {
    path: "/live-class/:sessionId/playback",
    element: <RouteShell Component={PlaybackRoomPage} paramKey="sessionId" />,
  },
  {
    path: "/live-class/:sessionId",
    element: <RouteShell Component={LiveSessionPage} paramKey="sessionId" />,
  },
  { path: "/wallet", element: <RouteShell Component={WalletPage} /> },
  // S-21 — the LEGACY /dashboard was a 100%-mock page (hardcoded sparklines,
  // fabricated schedule/credentials — zero API calls). Every role now has a
  // REAL home (ROLES[role].homeRoute — none of them is "dashboard", so no
  // loop), so /dashboard redirects there. pages/Dashboard.tsx stays on disk
  // and importable (nothing deleted) — it is just no longer routed.
  { path: "/dashboard", element: <RoleHomeRedirect /> },
  // Phase-A R3: dedicated role-home dashboards. role.tsx's homeRoute fields
  // point here so the post-login redirect lands on the correct surface
  // per role. The legacy /dashboard, /instructor, /admin, /parent routes
  // remain for student/instructor/admin/parent respectively.
  { path: "/super", element: <RouteShell Component={SuperAdminDashboard} /> },
  { path: "/content", element: <RouteShell Component={ContentManagerDashboard} /> },
  { path: "/ta", element: <RouteShell Component={TADashboard} /> },
  { path: "/support", element: <RouteShell Component={SupportDashboard} /> },
  { path: "/moderate", element: <RouteShell Component={ModeratorDashboard} /> },
  { path: "/org", element: <RouteShell Component={OrgDashboard} /> },
  { path: "/faculty-dashboard", element: <RouteShell Component={FacultyAdminDashboard} /> },
  {
    // S-21 — the connecting hub; ?tab= selects the section. The old player
    // (CoursePage) renders inside the hub's «محتوا» tab.
    path: "/course/:courseId",
    element: <RouteShell Component={CourseHubPage} paramKey="courseId" />,
  },
  { path: "/course", element: <RouteShell Component={CourseHubPage} /> },
  { path: "/instructor", element: <RouteShell Component={InstructorPage} /> },
  // R-NAV-3 (spec §4.5, §22.1) — retire the mock admissions wizard: redirect
  // to the real multi-step application form. PUBLIC→PUBLIC, so no auth flash.
  { path: "/admissions", element: <Navigate to="/apply" replace /> },
  // Phase B R6 (D80) — public anon application form (PUBLIC route, Q1.a).
  { path: "/apply", element: <RouteShell Component={ApplyPage} /> },
  // Phase B R6 (D80) — public anon status page (token in ?token=, type in ?type=).
  { path: "/track", element: <RouteShell Component={TrackPage} /> },
  // R-Notif (D100) — public password-claim landing (token in ?token=).
  { path: "/claim", element: <RouteShell Component={ClaimPage} /> },
  // R-AUTH-3 — public password-reset landing (token in ?token=).
  { path: "/reset", element: <RouteShell Component={ResetPage} /> },
  // S-SMS-OTP — phone-verification onboarding. WORKSPACE (auth-gated); `next`
  // carries the eventual destination route id (e.g. after a post-claim redirect).
  {
    path: "/verify-phone/:next",
    element: <RouteShell Component={VerifyPhonePage} paramKey="next" />,
  },
  { path: "/verify-phone", element: <RouteShell Component={VerifyPhonePage} /> },
  { path: "/credential", element: <RouteShell Component={CredentialPage} /> },
  // S-CRED W2 — سندِ چاپیِ دانشنامه. مسیرِ ۳بخشیِ ثابت‌پیشوند، پس پیش از
  // «/credential» تداخلی ندارد.
  // S-CRED W4 — از این موج سگمنتِ «credential» **SPLIT** است: خودِ /credential
  // عمومی شد (استعلامِ اصالت با QR) ولی همین صفحهٔ چاپ WORKSPACE ماند؛
  // `getRouteKind("credential", "print/…")` دقیقاً همین را برمی‌گرداند.
  {
    path: "/credential/print/:id",
    element: <RouteShell Component={DiplomaPrintPage} paramKey="id" />,
  },
  { path: "/search", element: <RouteShell Component={SearchPage} /> },
  { path: "/assessment", element: <RouteShell Component={AssessmentPage} /> },
  { path: "/community", element: <RouteShell Component={CommunityPage} /> },
  { path: "/analytics", element: <RouteShell Component={AnalyticsPage} /> },
  { path: "/authoring", element: <RouteShell Component={AuthoringPage} /> },
  { path: "/recordings", element: <RouteShell Component={RecordingsPage} /> },
  // Phase-15 R7: audit-log viewer. The page itself uses useAbility()
  // to render a denial state for users without read access, and the
  // api enforces the same gate via @Roles + @CheckPolicies. Both
  // layers must agree — defence in depth.
  { path: "/audit", element: <RouteShell Component={AuditPage} /> },
  // S-LAND-POLISH م۴ — GuestOnly: کاربرِ احرازشده به‌جای فرمِ ورود، خانهٔ
  // نقشِ خودش را می‌بیند (لندینگ اما برای همه باز است).
  {
    path: "/login",
    element: (
      <GuestOnly>
        <RouteShell Component={LoginPage} />
      </GuestOnly>
    ),
  },
  // R-AUTH-1 (spec §5.2, owner decision: application-only) — open
  // registration is retired; /register redirects to the real application
  // funnel. The api also rejects POST /v1/auth/register (server-enforced).
  {
    path: "/register",
    element: (
      <GuestOnly>
        <Navigate to="/apply" replace />
      </GuestOnly>
    ),
  },
  { path: "/forgot", element: <RouteShell Component={ForgotPage} /> },
  {
    path: "/verify-email",
    element: <RouteShell Component={VerifyEmailPage} />,
  },
  { path: "/2fa-setup", element: <RouteShell Component={TwoFactorPage} /> },
  { path: "/onboarding", element: <RouteShell Component={OnboardingPage} /> },
  { path: "/settings", element: <RouteShell Component={SettingsPage} /> },
  // S-2 nav fix — deep-link into a specific settings section (e.g.
  // /settings/security so the profile "امنیت و نشست‌ها" / 2FA-manage links
  // land on the Security tab instead of dead-ending on Profile). Sibling
  // param route of /settings (see the /course/:courseId pattern); the bare
  // /settings entry above stays registered for the sidebar/⌘K "تنظیمات".
  { path: "/settings/:tab", element: <RouteShell Component={SettingsPage} paramKey="tab" /> },
  // R-CAL2 — the unified, role-aware "my calendar" is the PRIMARY /calendar surface
  // (the "تقویم" nav id resolves here). The older events+weekly CalendarPage stays
  // reachable at /calendar/events for staff org-event authoring; /academic-calendar
  // (term windows) is also still reachable.
  { path: "/calendar", element: <RouteShell Component={UnifiedCalendarPage} /> },
  { path: "/calendar/events", element: <RouteShell Component={CalendarPage} /> },
  { path: "/academic-calendar", element: <RouteShell Component={AcademicCalendarPage} /> },
  { path: "/library", element: <RouteShell Component={LibraryPage} /> },
  { path: "/help", element: <RouteShell Component={HelpPage} /> },
  { path: "/pricing", element: <RouteShell Component={PricingPage} /> },
  // S9 — self-serve subscription checkout (auth-gated as WORKSPACE; any role).
  { path: "/checkout/:planId", element: <RouteShell Component={CheckoutPage} paramKey="planId" /> },
  { path: "/faculty", element: <RouteShell Component={FacultyPage} /> },
  { path: "/admin", element: <RouteShell Component={AdminDashboardPage} /> },
  // Admin governance/ops + super tools (replace the ComingSoon placeholders).
  { path: "/admin/roles", element: <RouteShell Component={RolesPermissionsPage} /> },
  { path: "/admin/calendar", element: <RouteShell Component={AdminCalendarPage} /> },
  { path: "/admin/integrations", element: <RouteShell Component={IntegrationsPage} /> },
  { path: "/admin/notif-templates", element: <RouteShell Component={NotifTemplatesPage} /> },
  { path: "/admin/broadcast", element: <RouteShell Component={BroadcastPage} /> },
  { path: "/ai-governance", element: <RouteShell Component={AiGovernancePage} /> },
  // S-AI-PROVIDER-1 — route-roles.ts: بدونِ ردیفِ اختصاصی، پیش‌فرضِ زیردرختِ
  // "admin" (admin + super_admin) اعمال می‌شود — دقیقاً همان @Roles("admin") بک‌اند.
  { path: "/admin/ai-usage", element: <RouteShell Component={AiUsagePage} /> },
  { path: "/monitoring", element: <RouteShell Component={SystemMonitoringPage} /> },
  { path: "/capabilities-status", element: <RouteShell Component={CapabilitiesStatusPage} /> },
  { path: "/analytics-admin", element: <RouteShell Component={AdminAnalyticsPage} /> },
  { path: "/super/tenants", element: <RouteShell Component={TenantsAdminPage} /> },
  { path: "/super/impersonate", element: <RouteShell Component={ImpersonatePage} /> },
  { path: "/super/multitenancy", element: <RouteShell Component={MultitenancyPage} /> },
  // Support / TA / moderator / content_manager / org / parent (replace ComingSoon).
  { path: "/tickets", element: <RouteShell Component={TicketsQueuePage} /> },
  { path: "/user-lookup", element: <RouteShell Component={UserLookupPage} /> },
  { path: "/grading-queue", element: <RouteShell Component={GradingQueuePage} /> },
  { path: "/flagged-reports", element: <RouteShell Component={FlaggedReportsPage} /> },
  { path: "/community-moderation", element: <RouteShell Component={CommunityModerationPage} /> },
  { path: "/auto-rules", element: <RouteShell Component={AutoRulesPage} /> },
  { path: "/moderation-log", element: <RouteShell Component={ModerationLogPage} /> },
  { path: "/content-review", element: <RouteShell Component={ContentReviewPage} /> },
  { path: "/content-analytics", element: <RouteShell Component={ContentAnalyticsPage} /> },
  { path: "/org/users", element: <RouteShell Component={OrgUsersPage} /> },
  { path: "/org/bulk-enroll", element: <RouteShell Component={OrgBulkEnrollPage} /> },
  { path: "/org/seats", element: <RouteShell Component={OrgSeatsPage} /> },
  { path: "/org/invoices", element: <RouteShell Component={OrgInvoicesPage} /> },
  { path: "/org/reports", element: <RouteShell Component={OrgReportsPage} /> },
  { path: "/parent/grades", element: <RouteShell Component={ParentGradesPage} /> },
  { path: "/parent/attendance", element: <RouteShell Component={ParentAttendancePage} /> },
  { path: "/parent/schedule", element: <RouteShell Component={ParentSchedulePage} /> },
  { path: "/parent/tuition", element: <RouteShell Component={ParentTuitionPage} /> },
  // R-SIS-1 — academic terms (نیم‌سال). Gated to admin+super_admin by the
  // /admin/* subtree default in route-roles.ts.
  { path: "/admin/terms", element: <RouteShell Component={TermsAdminPage} /> },
  // R-SIS-2 — curriculum builder. Gated to faculty_admin+admin+super_admin
  // by the "curriculum-builder" key in route-roles.ts.
  { path: "/curriculum-builder", element: <RouteShell Component={CurriculumBuilderPage} /> },
  // R-MARKET P1 — «بازار دانش» authoring studio. Gated to instructor/
  // content_manager/admin/super_admin by the "market" key in route-roles.ts.
  // The more-specific :id route is a sibling; react-router resolves it first.
  { path: "/market/studio", element: <RouteShell Component={MarketStudioPage} /> },
  // S-MKT-PRO-W1 — the گام‌به‌گام creation wizard. Literal /new ranks ahead of the
  // dynamic /market/studio/:id. Same author "market" role key gates it.
  { path: "/market/studio/new", element: <RouteShell Component={MarketCreateWizardPage} /> },
  // R-MARKET BUNDLES — author bundle studio. The literal /studio/bundles + its :id
  // child rank ahead of the dynamic /market/studio/:id, so react-router resolves them
  // first. Same author "market" role key gates the subtree (the API 404s cross-author).
  { path: "/market/studio/bundles", element: <RouteShell Component={MarketStudioBundlesPage} /> },
  {
    path: "/market/studio/bundles/:id",
    element: <RouteShell Component={MarketBundleEditorPage} paramKey="id" />,
  },
  // S-MKT-PRO-W1 — the routed studio SHELL. The bare :id canonicalises to
  // …/overview inside StudioLayout (old links keep working); the seven literal
  // tab children resolve ahead of the :id/preview and :id/record siblings only
  // by being DIFFERENT literal segments — no collision (react-router ranks
  // literals over dynamics regardless of declaration order).
  {
    path: "/market/studio/:id",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  {
    path: "/market/studio/:id/overview",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  {
    path: "/market/studio/:id/curriculum",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  {
    path: "/market/studio/:id/content",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  // E1 (S-MKT-PRO زیراپیک E) — tab 8 «آزمون‌ها» (in-course exercises/exams).
  {
    path: "/market/studio/:id/assessments",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  {
    path: "/market/studio/:id/pricing",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  {
    path: "/market/studio/:id/coupons",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  {
    path: "/market/studio/:id/analytics",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  {
    path: "/market/studio/:id/settings",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  // S-CT-W1 — tab «الترجمة العربية» (human review of the machine-translated
  // fields). StudioLayout resolves the active tab from the pathname, so the tab
  // button lands on NotFound until this literal route exists. Same "market" key.
  {
    path: "/market/studio/:id/translations",
    element: <RouteShell Component={MarketStudioShellPage} paramKey="id" />,
  },
  // R-MARKET P2 — author "preview as a student" (more specific than :id; react-
  // router resolves it first). Same "market" role key gates the subtree.
  {
    path: "/market/studio/:id/preview",
    element: <RouteShell Component={MarketCoursePreviewPage} paramKey="id" />,
  },
  // R-MARKET P7 (FINAL) — in-browser recording studio (more specific than :id; resolved first).
  // Same author "market" role key gates the subtree.
  {
    path: "/market/studio/:id/record",
    element: <RouteShell Component={MarketRecordStudioPage} paramKey="id" />,
  },
  // S-MKT-PRO-W3 — the EDL timeline for ONE uploaded clip. Three segments after :id, so it
  // resolves ahead of the studio shell; the page reads :assetId itself via useParams.
  {
    path: "/market/studio/:id/edit/:assetId",
    element: <RouteShell Component={MarketEditProjectPage} paramKey="id" />,
  },
  // R-MARKET OPEN-RESPONSE (Gap 9) — author STAFF grading queue for one course. Static
  // 2-segment prefix → resolves ahead of the dynamic /market/course/:id. Gated to the author
  // roles by the "market/grading-queue" key in route-roles.ts; the API 404s a cross-author course.
  {
    path: "/market/grading-queue/:id",
    element: <RouteShell Component={MarketGradingQueuePage} paramKey="id" />,
  },
  // R-MARKET P3 — reviewer queue + per-course review. Gated to content_manager/
  // admin/super_admin by the "market/review" 2-segment key in route-roles.ts.
  { path: "/market/review", element: <RouteShell Component={MarketReviewQueuePage} /> },
  {
    path: "/market/review/:id",
    element: <RouteShell Component={MarketReviewCoursePage} paramKey="id" />,
  },
  // R-MARKET P4 — PUBLIC marketplace. The bare /market catalog + /market/course/:id
  // detail are PUBLIC (browsable LOGGED-OUT); react-router resolves the more-specific
  // /market/studio* + /market/review* siblings above first, so they're unaffected.
  { path: "/market", element: <RouteShell Component={MarketplacePage} /> },
  {
    path: "/market/course/:id",
    element: <RouteShell Component={MarketCourseDetailPage} paramKey="id" />,
  },
  // S-MKT-PRO-W2 — PUBLIC «ویترین مدرس» (/market/instructor/:userId). Reachable
  // LOGGED-OUT like /market/course/:id ("instructor" is NOT in AUTHED_MARKET_SUBPATHS);
  // the API 404s any user without a published course, so nothing leaks. No prefix
  // collision with the other 2-segment market routes.
  {
    path: "/market/instructor/:id",
    element: <RouteShell Component={MarketInstructorProfilePage} paramKey="id" />,
  },
  // R-MARKET BUNDLES — PUBLIC bundle catalog (/market/bundles) + bundle detail
  // (/market/bundles/:id). Both reachable LOGGED-OUT (route-classification keeps the
  // "bundles" market sub-path PUBLIC). The literal /market/bundles ranks ahead of the
  // dynamic /market/bundles/:id; neither collides with /market/course/:id.
  { path: "/market/bundles", element: <RouteShell Component={MarketBundlesPage} /> },
  {
    path: "/market/bundles/:id",
    element: <RouteShell Component={MarketBundleDetailPage} paramKey="id" />,
  },
  // R-MARKET P5 — economics surfaces (static 2-segment paths; resolved ahead of the
  // dynamic /market/course/:id). All WORKSPACE (auth-gated) + role-keyed in route-roles.
  { path: "/market/my-courses", element: <RouteShell Component={MarketMyCoursesPage} /> },
  // S-MKT-PRO-W4 — the buyer's purchase history + receipts (static 2-segment path).
  { path: "/market/purchases", element: <RouteShell Component={MarketPurchasesPage} /> },
  // S-MKT-PRO-W8 — the merged money timeline (static 2-segment path).
  { path: "/market/transactions", element: <RouteShell Component={MarketTransactionsPage} /> },
  { path: "/market/wishlist", element: <RouteShell Component={MarketWishlistPage} /> },
  // S-MKT-PRO-W9 — the buyer's cart (static 2-segment path, same premise as the wishlist).
  { path: "/market/cart", element: <RouteShell Component={MarketCartPage} /> },
  { path: "/market/earnings", element: <RouteShell Component={MarketEarningsPage} /> },
  { path: "/market/admin", element: <RouteShell Component={MarketAdminPage} /> },
  // S-STUDIO-P1-W1 — ذخیره‌سازیِ استودیو. pathKey فقط از دو سگمنتِ اول ساخته می‌شود
  // ("market/admin")، پس همان ROUTE_ROLES کلیدِ /market/admin را به ارث می‌برد — نیازی به
  // ردیفِ تازه در route-roles.ts نیست (اصلاحیهٔ ۳ مالک، تأییدشده روی بک‌اند و فرانت هر دو).
  { path: "/market/admin/storage", element: <RouteShell Component={StudioStorageAdminPage} /> },
  // S-LIVE-P1-W1 — سقفِ ظرفیتِ کلاسِ زنده (سوپرادمین/ادمین).
  { path: "/live-class/admin", element: <RouteShell Component={LiveClassCapacityAdminPage} /> },
  // R-MARKET P6 — the enrolled buyer's COURSE PLAYER (static 2-segment prefix → resolves
  // ahead of the dynamic /market/course/:id). WORKSPACE + "market/learn" role key (any
  // authed role); the API 404s non-enrolled callers.
  {
    path: "/market/learn/:id",
    element: <RouteShell Component={MarketCoursePlayerPage} paramKey="id" />,
  },
  // S-MKT-PRO-E2 — the graduate's printable certificate (same premise as the player).
  {
    path: "/market/certificate/:id",
    element: <RouteShell Component={MarketCertificatePage} paramKey="id" />,
  },
  // S-MKT-PRO-E4 — the buyer's report card for one purchased course (same premise again:
  // static 2-segment prefix, WORKSPACE, and the API is the real gate).
  {
    path: "/market/report-card/:id",
    element: <RouteShell Component={MarketReportCardPage} paramKey="id" />,
  },
  // R-SIS-5b — service desk (one page; param drives inbox/new/detail).
  { path: "/service-desk", element: <RouteShell Component={ServiceDeskPage} /> },
  { path: "/service-desk/new", element: <RouteShell Component={ServiceDeskPage} /> },
  { path: "/service-desk/:id", element: <RouteShell Component={ServiceDeskPage} paramKey="id" /> },
  // R-SIS-5b — workflow builder. Gated to admin+super_admin by /admin/* default.
  { path: "/admin/workflows", element: <RouteShell Component={WorkflowsAdminPage} /> },
  // R-SIS — degree policies (caps + probation thresholds). admin/* default gate.
  { path: "/admin/degree-policies", element: <RouteShell Component={DegreePoliciesAdminPage} /> },
  // R-PAY-1d — admin tuition tariff config. admin/* default gate.
  { path: "/admin/tuition-config", element: <RouteShell Component={TuitionConfigAdminPage} /> },
  // S-CARD W0-cfg-b گامِ ۲ — تنظیماتِ عضویت. دو نقش (finance/membership manager)؛
  // route-roles.ts دقیقاً همین دو + super_admin را مجاز می‌کند.
  { path: "/admin/membership-config", element: <RouteShell Component={MembershipConfigPage} /> },
  // S-ASSESS-W3b — کنسولِ مدیرِ کانونِ سنجش (پروژه/داوطلب/تمرین/تخصیص).
  // route-roles: "admin/assess-center" → assessment_manager+admin+super_admin.
  { path: "/admin/assess-center", element: <RouteShell Component={AssessCenterAdminPage} /> },
  // S-CARD فاز A W5 — self-service (هر کاربرِ واردشده) + کنسولِ اپراتور
  // (route-roles: membership_operator+membership_manager).
  { path: "/membership", element: <RouteShell Component={MembershipPage} /> },
  { path: "/membership/directory", element: <RouteShell Component={MembershipDirectoryPage} /> },
  { path: "/admin/membership-queue", element: <RouteShell Component={MembershipQueuePage} /> },
  // S-ASSESS-W3b — کنسولِ ارزیاب (بارِ عمداً ungated، الگویِ /audit — گیتِ واقعی
  // سمتِ سرور: myAssignments/roster/exerciseDetail برایِ غیرِارزیاب ۴۰۴ می‌دهند).
  { path: "/assess-center", element: <RouteShell Component={AssessorConsolePage} /> },
  // route-roles: "assess-center/exercise" → assessor+assessment_manager+super_admin.
  {
    path: "/assess-center/exercise/:id",
    element: <RouteShell Component={AssessExerciseRosterPage} paramKey="id" />,
  },
  // دو پارامتر (candidateId+exerciseId) — RouteShell فقط یکی را forward می‌کند
  // (الگویِ EditProjectPage)؛ خودِ صفحه useParams مستقیم می‌خواند.
  // route-roles: "assess-center/candidate" → assessor+assessment_manager+super_admin.
  {
    path: "/assess-center/candidate/:candidateId/exercise/:exerciseId",
    element: <RouteShell Component={AssessRatingPage} />,
  },
  // سفرِ خودِ داوطلب — عمداً هم‌مسیرِ "assess-center" با کنسول بالا (نکتهٔ route-roles).
  { path: "/assess-center/my", element: <RouteShell Component={AssessCandidateJourneyPage} /> },
  // S-ASSESS-W4b — اتاقِ جلسهٔ واش‌آپ. route-roles: "assess-center/washup" →
  // assessor+assessment_manager+admin+super_admin (گیتِ واقعیِ دیدن‌پذیری سمتِ سرور است).
  {
    path: "/assess-center/washup/:sessionId",
    element: <RouteShell Component={AssessWashupRoomPage} paramKey="sessionId" />,
  },
  // S-ASSESS-W5b — گزارشِ منتشرشدهٔ خودِ داوطلب (ر۱، self-service، بدونِ پارامتر).
  { path: "/assess-center/my-report", element: <RouteShell Component={AssessMyReportPage} /> },
  // S-ASSESS-W5b — گزارشِ داخلیِ کاملِ مدیر (ر۲). دو پارامتر (candidateId+projectId)
  // — همان الگویِ AssessRatingPage: RouteShell فقط یکی forward می‌کند، پس اینجا
  // paramKey داده نمی‌شود و خودِ صفحه useParams مستقیم می‌خواند.
  // route-roles: "assess-center/reports" → assessment_manager+admin+super_admin.
  {
    path: "/assess-center/reports/:candidateId/:projectId",
    element: <RouteShell Component={AssessManagerReportPage} />,
  },
  // Phase B R1 Commit I (D62) — 4 nested admin academic routes (lazy
  // per D61 Constraint #2 — land in admin-academic chunk).
  { path: "/admin/schools", element: <RouteShell Component={SchoolsAdminPage} /> },
  { path: "/admin/faculties", element: <RouteShell Component={FacultiesAdminPage} /> },
  { path: "/admin/departments", element: <RouteShell Component={DepartmentsAdminPage} /> },
  { path: "/admin/programs", element: <RouteShell Component={ProgramsAdminPage} /> },
  { path: "/admin/plans", element: <RouteShell Component={PlansAdminPage} /> },
  // Phase B R2 (D65) — Offerings + Cohorts admin routes.
  { path: "/admin/offerings", element: <RouteShell Component={OfferingsAdminPage} /> },
  { path: "/admin/cohorts", element: <RouteShell Component={CohortsAdminPage} /> },
  // Phase B R3.a Commit I (D68 + D69) — Identity admin routes.
  { path: "/admin/profiles", element: <RouteShell Component={ProfilesAdminPage} /> },
  { path: "/admin/students", element: <RouteShell Component={StudentsAdminPage} /> },
  { path: "/admin/instructors", element: <RouteShell Component={InstructorsAdminPage} /> },
  // Phase B R3.b Commit G (D71) — Applications inbox.
  { path: "/admin/applications", element: <RouteShell Component={ApplicationsAdminPage} /> },
  { path: "/admin/hr", element: <RouteShell Component={HrAdminPage} /> },
  // Phase B R4 Commit E (D73) — Enrollments admin.
  { path: "/admin/enrollments", element: <RouteShell Component={EnrollmentsAdminPage} /> },
  // Phase C A.1 (D91) — course content authoring (picker + per-course editor).
  { path: "/admin/course-content", element: <RouteShell Component={CourseContentAdminPage} /> },
  {
    path: "/admin/course-content/:courseId",
    element: <RouteShell Component={CourseContentAdminPage} paramKey="courseId" />,
  },
  // Phase C A.4 (D93) — gradebook (picker + per-course; role-aware view).
  { path: "/gradebook", element: <RouteShell Component={GradebookPage} /> },
  {
    path: "/gradebook/:courseId",
    element: <RouteShell Component={GradebookPage} paramKey="courseId" />,
  },
  { path: "/parent", element: <RouteShell Component={ParentDashboard} /> },
  {
    path: "/officehours",
    element: <RouteShell Component={OfficeHoursPage} />,
  },
  { path: "/events", element: <RouteShell Component={EventsPage} /> },
  { path: "/about", element: <RouteShell Component={AboutPage} /> },
  // S-LANDING-2 — فهرستِ کاملِ امکانات و صفحهٔ اختصاصیِ هر قابلیت. مسیرِ
  // پارامتری بعد از مسیرِ ساده می‌آید تا `/features` خودش «اسلاگ» تعبیر نشود.
  { path: "/features", element: <RouteShell Component={FeaturesIndexPage} /> },
  {
    path: "/features/:slug",
    element: <RouteShell Component={FeatureDetailPage} paramKey="slug" />,
  },
  // S-LANDING-2 فاز ۲ — صفحاتی که فوترِ عمومی به آن‌ها لینک می‌دهد.
  { path: "/contact", element: <RouteShell Component={ContactPage} /> },
  { path: "/terms", element: <RouteShell Component={TermsPage} /> },
  { path: "/privacy", element: <RouteShell Component={PrivacyPage} /> },
  { path: "/faq", element: <RouteShell Component={FaqPage} /> },
  // مجریِ فنی/دارندهٔ حقِ سکو — عمداً جدا از /about (آن یکی سازمانِ بهره‌بردار
  // را معرفی می‌کند، این یکی شرکتی را که سکو محصولِ آن است).
  { path: "/vista", element: <RouteShell Component={VistaPage} /> },
  { path: "/schools", element: <RouteShell Component={SchoolsPage} /> },
  { path: "/labs", element: <RouteShell Component={LabsPage} /> },
  // S-UX-WAVE1 — /virtuallab بود صفحهٔ ساختگی با ادعای «GPU سرور فعال» که با
  // هیچ‌جای واقعی برنامه لینک نمی‌شد؛ آزمایشگاه مجازی واقعی همین /labs است.
  { path: "/virtuallab/:labId", element: <Navigate to="/labs" replace /> },
  { path: "/virtuallab", element: <Navigate to="/labs" replace /> },
  { path: "/research", element: <RouteShell Component={ResearchPage} /> },
  // R-NAV-4 (spec §3.4/3.5) — real "more" hub (was a broken bottom-nav slot
  // that fell through to the landing page).
  { path: "/more", element: <RouteShell Component={MoreHubPage} /> },
  { path: "/inbox", element: <RouteShell Component={NotificationsPage} /> },
  { path: "/messages", element: <RouteShell Component={MessagesPage} /> },
  { path: "/bookmarks", element: <RouteShell Component={BookmarksPage} /> },
  {
    path: "/achievements",
    element: <RouteShell Component={AchievementsPage} />,
  },
  { path: "/portfolio", element: <RouteShell Component={PortfolioPage} /> },
  { path: "/submission", element: <RouteShell Component={SubmissionPage} /> },
  { path: "/profile", element: <RouteShell Component={ProfilePage} /> },
  // R-SETTINGS — PUBLIC privacy-gated profile card. PUBLIC route (see
  // route-classification "u"); the page handles 404 (private) / 401 (members-only
  // for a guest) / 200 (visible) entirely from the API response.
  {
    path: "/u/:userId",
    element: <RouteShell Component={PublicProfilePage} paramKey="userId" />,
  },
  // R-CONNECT — «شبکهٔ من» (connect hub). WORKSPACE (auth-gated by elimination);
  // open to any authenticated role (no route-roles key — it's a shared account
  // surface like /messages, /inbox).
  { path: "/network", element: <RouteShell Component={NetworkPage} /> },
  { path: "/transcript", element: <RouteShell Component={TranscriptPage} /> },
  // R-PAY-1d — student tuition balance + pay.
  { path: "/tuition", element: <RouteShell Component={TuitionPage} /> },
  // R-PAY-2c — student loans / welfare review.
  { path: "/tuition/loans", element: <RouteShell Component={LoansPage} /> },
  // R-PAY-2d — financial clearance.
  { path: "/tuition/clearance", element: <RouteShell Component={ClearancePage} /> },
  // R-PAY-2f — tuition refunds.
  { path: "/tuition/refund", element: <RouteShell Component={RefundPage} /> },
  // R-PAYROLL-1c — payslips + decrees.
  { path: "/payroll/my-payslips", element: <RouteShell Component={PayslipsPage} /> },
  { path: "/admin/finance/decrees", element: <RouteShell Component={PayrollDecreesPage} /> },
  { path: "/admin/finance/withdrawals", element: <RouteShell Component={WithdrawalQueuePage} /> },
  { path: "/admin/finance/payroll-config", element: <RouteShell Component={PayrollConfigPage} /> },
  // R-PAYROLL-2a — teaching claims (instructor self-service + manager queue).
  { path: "/payroll/claims", element: <RouteShell Component={ClaimsPage} /> },
  { path: "/admin/finance/claims", element: <RouteShell Component={TeachingClaimsPage} /> },
  { path: "/admin/finance/payroll", element: <RouteShell Component={PayrollRunPage} /> },
  { path: "/admin/finance/treasury", element: <RouteShell Component={TreasuryPage} /> },
  { path: "/admin/commission", element: <RouteShell Component={CommissionPage} /> },
  { path: "/admin/regulation", element: <RouteShell Component={RegulationAdminPage} /> },
  { path: "/my-path", element: <RouteShell Component={MyPathPage} /> },
  { path: "/grade-submission", element: <RouteShell Component={GradeSubmissionPage} /> },
  { path: "/my-grades", element: <RouteShell Component={MyGradesPage} /> },
  // R-COMPLY W3 — student's own attendance (per-section tally + ۳/۱۶ warning).
  { path: "/my-attendance", element: <RouteShell Component={MyAttendancePage} /> },
  { path: "/admin/stage-progress", element: <RouteShell Component={StageProgressAdminPage} /> },
  { path: "/admin/people", element: <RouteShell Component={PeoplePage} /> },
  // R-MON W6 — «پروندهٔ افراد»: role-aware person-360 (student/instructor),
  // faculty_admin scoped server-side. Route id kept stable as the known entry.
  { path: "/admin/student-overview", element: <RouteShell Component={StudentOverviewPage} /> },
  // R-ACAD — گرایش management (program picker → per-program CRUD).
  { path: "/admin/specializations", element: <RouteShell Component={SpecializationsPage} /> },
  // R-ACAD — داده‌های مرجع (گروه‌های آموزشی + مقاطع تحصیلی CRUD).
  { path: "/admin/reference-data", element: <RouteShell Component={ReferenceDataPage} /> },
  // chart-manual.md — کتابخانه وزارت علوم (3268 PDF browser + detail + instantiate).
  { path: "/admin/ministry-library", element: <RouteShell Component={MinistryLibraryPage} /> },
  { path: "/admin/ministry-library/:id", element: <RouteShell Component={MinistryProgramDetailPage} /> },
  { path: "/admin/users", element: <RouteShell Component={UsersPage} /> },
  // R-MON W4 — admin parent↔student link onboarding (admin/super_admin/support).
  { path: "/admin/parent-links", element: <RouteShell Component={ParentLinksPage} /> },
  {
    path: "/degree-audit",
    element: <RouteShell Component={DegreeAuditPage} />,
  },
  // چارت درسی من — student self-view of the full گرایش-filtered chart.
  { path: "/my-chart", element: <RouteShell Component={MyChartPage} /> },
  // Student services — real interactive pages (replace the ComingSoon placeholders).
  { path: "/cognitive-profile", element: <RouteShell Component={CognitiveProfilePage} /> },
  { path: "/adaptive-path", element: <RouteShell Component={AdaptivePathPage} /> },
  { path: "/assignments", element: <RouteShell Component={AssignmentsPage} /> },
  // Instructor teaching tools (replace the ComingSoon placeholders).
  { path: "/instructor-dashboard", element: <RouteShell Component={InstructorDashboardPage} /> },
  { path: "/question-bank", element: <RouteShell Component={QuestionBankPage} /> },
  { path: "/assessment-builder", element: <RouteShell Component={AssessmentBuilderPage} /> },
  { path: "/rubric-builder", element: <RouteShell Component={RubricBuilderPage} /> },
  { path: "/roster", element: <RouteShell Component={ClassRosterPage} /> },
  { path: "/attendance", element: <RouteShell Component={AttendancePage} /> },
  { path: "/class-analytics", element: <RouteShell Component={ClassAnalyticsPage} /> },
  { path: "/item-analysis", element: <RouteShell Component={ItemAnalysisPage} /> },
  { path: "/copilot", element: <RouteShell Component={InstructorCopilotPage} /> },
  // Faculty-admin teaching/ops tools (replace the ComingSoon placeholders).
  { path: "/admin/courses", element: <RouteShell Component={CourseCatalogMasterPage} /> },
  { path: "/instructor-assignment", element: <RouteShell Component={InstructorAssignmentPage} /> },
  { path: "/probation-review", element: <RouteShell Component={ProbationReviewPage} /> },
  { path: "/faculty-reports", element: <RouteShell Component={FacultyReportsPage} /> },
  { path: "/schedule", element: <RouteShell Component={WeeklySchedulePage} /> },
  {
    path: "/registration",
    element: <RouteShell Component={RegistrationPage} />,
  },
  // R-SIS-3d — حذف و اضافه shares the RegistrationPage (mode from the route id).
  {
    path: "/add-drop",
    element: <RouteShell Component={RegistrationPage} />,
  },
  { path: "/career", element: <RouteShell Component={CareerPage} /> },
  {
    path: "/financial-aid",
    element: <RouteShell Component={FinancialAidPage} />,
  },
  { path: "/wellness", element: <RouteShell Component={WellnessPage} /> },
  // WELL-1 — wellness test catalogue (grouped by category). Static segment, so
  // it resolves ahead of /wellness/test/:id.
  { path: "/wellness/tests", element: <RouteShell Component={WellnessTestsPage} /> },
  // WELL-1 — take a wellness self-screening test. testId in the path param.
  {
    path: "/wellness/test/:id",
    element: <RouteShell Component={WellnessTestPage} paramKey="id" />,
  },
  // WELL-1 — self-only result history (static segment → resolves ahead of
  // /wellness/result/:id) + one result detail (resultId in the path param).
  { path: "/wellness/results", element: <RouteShell Component={WellnessResultsPage} /> },
  {
    path: "/wellness/result/:id",
    element: <RouteShell Component={WellnessResultPage} paramKey="id" />,
  },
  // S-ASSESS-CENTER (W2) — competency assessment catalogue. /assess is a bare
  // alias for the same catalogue page as /assess/tools (both static segments,
  // so both resolve ahead of the dynamic /assess/tool/:slug below).
  { path: "/assess", element: <RouteShell Component={AssessToolsPage} /> },
  { path: "/assess/tools", element: <RouteShell Component={AssessToolsPage} /> },
  // S-ASSESS-CENTER (W2) — take/attempt one tool. toolSlug in the path param
  // (the page component reads a `slug` prop — matches AssessTool.tsx).
  {
    path: "/assess/tool/:slug",
    element: <RouteShell Component={AssessToolPage} paramKey="slug" />,
  },
  // S-ASSESS-CENTER (W2) — self-only result history (static segment → resolves
  // ahead of /assess/result/:id) + one result detail (resultId in the path param).
  { path: "/assess/results", element: <RouteShell Component={AssessResultsPage} /> },
  {
    path: "/assess/result/:id",
    element: <RouteShell Component={AssessResultPage} paramKey="id" />,
  },
  // WELL-4 — counselor (مشاور دانشگاه) workspace. The inbox is the role's home;
  // it hosts the own-appointments queue + the open crisis queue + scoped student
  // summaries. route-roles.ts gates all three to counselor/admin/super_admin.
  // The two sibling sidebar destinations render the SAME page; CounselorInbox
  // self-selects the active tab from the route id (useCurrentRoute), so the
  // counselor nav is fully live + coherent (no dead-end placeholders).
  { path: "/counselor-inbox", element: <RouteShell Component={CounselorInboxPage} /> },
  { path: "/counseling-appointments", element: <RouteShell Component={CounselorInboxPage} /> },
  { path: "/crisis-queue", element: <RouteShell Component={CounselorInboxPage} /> },
  // R-MON W9 — counselor caseload / monitoring. route-roles.ts gates it to
  // counselor/admin/super_admin; the backend scopes every read to the caller's
  // OWN caseload + LINKED students (404 no-leak).
  { path: "/counselor-caseload", element: <RouteShell Component={CounselorCaseloadPage} /> },
  { path: "/alumni", element: <RouteShell Component={AlumniPage} /> },
  { path: "/hackathons", element: <RouteShell Component={HackathonsPage} /> },
  { path: "/honor-code", element: <RouteShell Component={HonorCodePage} /> },
  // R-NAV-2 — "coming soon" placeholders for the redesigned sidebars'
  // not-yet-built destinations. Generated from PLACEHOLDER_ROUTES so the
  // route table and the sidebar links share one source of truth. Each key is
  // a 1- or 2-segment path; ComingSoon derives its title from the same map.
  // Placed before the catch-all so they match ahead of "*".
  ...Object.keys(PLACEHOLDER_ROUTES).map((key) => ({
    path: "/" + key,
    element: <RouteShell Component={ComingSoonPage} />,
  })),
];

// S-21 — the registered path list, exported for the nav-coherence vitest
// (tests/nav/nav-coherence.test.ts): every sidebar / bottom-nav / command
// link must resolve to one of these paths, so a renamed route that leaves a
// stale nav link behind fails the BLOCKING gate instead of shipping a
// dead-end. Paths only (no elements) — importing this stays side-effect-light.
export const ROUTE_PATHS: readonly string[] = routes.map((r) => r.path);

const router = createBrowserRouter([
  {
    element: <LayoutWithChrome />,
    children: routes,
  },
  // R-NAV-6 (spec §4.10) — catch-all 404 as a TOP-LEVEL sibling, OUTSIDE the
  // AppShell layout. Known paths match the layout's children first (more
  // specific); only genuinely-unknown paths fall here. Standalone so the
  // workspace auth gate can't bounce an anonymous visitor to /login.
  { path: "*", element: <NotFoundPage /> },
]);

export const AppRouter: React.FC = () => <RouterProvider router={router} />;
